import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  DENY_ALL,
  DIMENSION_KEYS,
  DIMENSION_LABELS,
  GRANT_ALL,
  loadConsent,
  pushConsentUpdate,
  saveConsent,
  type ConsentDimensions,
  type StoredConsent,
} from '../lib/consent';

type ConsentManagerValue = {
  decision: StoredConsent | null;
  open(): void;
};

const ConsentManagerContext = createContext<ConsentManagerValue | null>(null);

export function useConsentManager(): ConsentManagerValue {
  const value = useContext(ConsentManagerContext);
  if (!value) {
    throw new Error('useConsentManager must be used inside <ConsentProvider>');
  }
  return value;
}

type ProviderProps = { children: ReactNode };

export function ConsentProvider({ children }: ProviderProps) {
  const [decision, setDecision] = useState<StoredConsent | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    setDecision(stored);
    setIsOpen(stored === null);
    setHydrated(true);
  }, []);

  const handleSave = useCallback((consent: ConsentDimensions) => {
    const saved = saveConsent(consent);
    pushConsentUpdate(consent);
    setDecision(saved);
    setIsOpen(false);
  }, []);

  const value = useMemo<ConsentManagerValue>(() => ({
    decision,
    open: () => setIsOpen(true),
  }), [decision]);

  return (
    <ConsentManagerContext.Provider value={value}>
      {children}
      {hydrated && isOpen ? (
        <ConsentDialog
          initialConsent={decision?.consent ?? DENY_ALL}
          allowDismiss={decision !== null}
          onDismiss={() => setIsOpen(false)}
          onSave={handleSave}
        />
      ) : null}
    </ConsentManagerContext.Provider>
  );
}

type DialogProps = {
  initialConsent: ConsentDimensions;
  allowDismiss: boolean;
  onDismiss(): void;
  onSave(consent: ConsentDimensions): void;
};

function ConsentDialog({ initialConsent, allowDismiss, onDismiss, onSave }: DialogProps) {
  const [mode, setMode] = useState<'summary' | 'customise'>('summary');
  const [draft, setDraft] = useState<ConsentDimensions>(initialConsent);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    // Move focus into the dialog on open so screen readers announce it.
    const first = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    return () => previouslyFocused.current?.focus?.();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && allowDismiss) {
        e.preventDefault();
        onDismiss();
        return;
      }
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('aria-hidden'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [allowDismiss, onDismiss]);

  return (
    <div
      className="consent-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        // Click outside the panel: dismiss if allowed, otherwise nothing.
        if (e.target === e.currentTarget && allowDismiss) onDismiss();
      }}
    >
      <div
        ref={dialogRef}
        className="consent-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-title"
        aria-describedby="consent-blurb"
      >
        <h2 id="consent-title">Cookies on Guess My Time</h2>
        {mode === 'summary' ? (
          <SummaryView
            allowDismiss={allowDismiss}
            onAcceptAll={() => onSave(GRANT_ALL)}
            onRejectAll={() => onSave(DENY_ALL)}
            onCustomise={() => setMode('customise')}
            onDismiss={onDismiss}
          />
        ) : (
          <CustomiseView
            draft={draft}
            onChange={setDraft}
            onSave={() => onSave(draft)}
            onBack={() => setMode('summary')}
          />
        )}
      </div>
    </div>
  );
}

type SummaryProps = {
  allowDismiss: boolean;
  onAcceptAll(): void;
  onRejectAll(): void;
  onCustomise(): void;
  onDismiss(): void;
};

function SummaryView({ allowDismiss, onAcceptAll, onRejectAll, onCustomise, onDismiss }: SummaryProps) {
  return (
    <>
      <p id="consent-blurb">
        We use a small set of cookies and analytics tags to understand how people find our pages and
        whether the sweepstake flow works. None of them process payments. Strictly necessary cookies
        (sign-in, antiforgery) stay on so the site works. You can change your mind any time from the
        footer.
      </p>
      <div className="consent-actions">
        <button type="button" className="btn btn-primary" onClick={onAcceptAll}>
          Accept all
        </button>
        <button type="button" className="btn" onClick={onRejectAll}>
          Reject all
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCustomise}>
          Customise
        </button>
      </div>
      <p className="consent-foot">
        <a href="/privacy">Read our privacy notice</a>
        {allowDismiss ? (
          <>
            {' · '}
            <button type="button" className="link-button" onClick={onDismiss}>
              Close
            </button>
          </>
        ) : null}
      </p>
    </>
  );
}

type CustomiseProps = {
  draft: ConsentDimensions;
  onChange(next: ConsentDimensions): void;
  onSave(): void;
  onBack(): void;
};

function CustomiseView({ draft, onChange, onSave, onBack }: CustomiseProps) {
  const toggle = (key: typeof DIMENSION_KEYS[number]) => {
    onChange({
      ...draft,
      [key]: draft[key] === 'granted' ? 'denied' : 'granted',
    });
  };
  return (
    <>
      <p id="consent-blurb">
        Pick the things you are happy for us to use. Nothing is on by default. Strictly necessary
        cookies stay on regardless so the site works.
      </p>
      <ul className="consent-list" aria-label="Cookie categories">
        <li className="consent-row consent-row-locked">
          <div className="consent-row-text">
            <strong>Strictly necessary</strong>
            <span>Sign-in cookie and antiforgery token. Required for the site to work.</span>
          </div>
          <span className="consent-locked-tag" aria-hidden="true">Always on</span>
          <span className="sr-only">Always on, cannot be disabled.</span>
        </li>
        {DIMENSION_KEYS.map((key) => {
          const label = DIMENSION_LABELS[key];
          const checked = draft[key] === 'granted';
          const inputId = `consent-${key}`;
          return (
            <li key={key} className="consent-row">
              <div className="consent-row-text">
                <label htmlFor={inputId}>
                  <strong>{label.title}</strong>
                </label>
                <span>{label.blurb}</span>
              </div>
              <input
                id={inputId}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(key)}
                aria-describedby={`${inputId}-desc`}
              />
              <span id={`${inputId}-desc`} className="sr-only">{label.blurb}</span>
            </li>
          );
        })}
      </ul>
      <div className="consent-actions">
        <button type="button" className="btn btn-primary" onClick={onSave}>
          Save preferences
        </button>
        <button type="button" className="btn btn-ghost" onClick={onBack}>
          Back
        </button>
      </div>
    </>
  );
}
